// Dropdowns of the "KYC and Risk Assessment Module" sheet. Values must match
// the codes scored in server/utils/customerRiskScoring.js; `label` is the
// sheet's dropdown text and `risk` the colour the sheet gives that option.

// Client
export const NATIONALITY_CATEGORIES = [
  { v: 'namibian',             label: 'Namibian',             risk: 'low' },
  { v: 'foreign',              label: 'Foreign',              risk: 'high' },
  { v: 'foreign_grey_listed',  label: 'Foreign Grey Listed',  risk: 'high' },
  { v: 'foreign_black_listed', label: 'Foreign Black Listed', risk: 'high' }
];

export const PIP_OPTIONS = [
  { v: 'no',  label: 'No',  risk: 'low' },
  { v: 'yes', label: 'Yes', risk: 'high' }
];

export const PIP_ASSOCIATE_OPTIONS = [
  { v: 'no',               label: 'No',               risk: 'low' },
  { v: 'family_member',    label: 'Family Member',    risk: 'high' },
  { v: 'child',            label: 'Child',            risk: 'high' },
  { v: 'spouse',           label: 'Spouse',           risk: 'high' },
  { v: 'business_partner', label: 'Business Partner', risk: 'high' }
];

export const OCCUPATIONS = [
  { v: 'employed',        label: 'Employed',                  risk: 'low' },
  { v: 'student',         label: 'Student',                   risk: 'low' },
  { v: 'pensioner',       label: 'Pension',                   risk: 'low' },
  { v: 'business_person', label: 'Businessmen/Businesswoman', risk: 'high' }
];

export const SOURCES_OF_INCOME = [
  { v: 'salary',        label: 'Salary from formal employment', risk: 'low' },
  { v: 'self_employed', label: 'Self Employed',                 risk: 'high' },
  { v: 'pension',       label: 'Pension',                       risk: 'low' }
];

export const TRANSACTION_SOURCES_OF_FUNDS = [
  { v: 'salary',            label: 'Salary',            risk: 'low' },
  { v: 'savings',           label: 'Savings',           risk: 'medium' },
  { v: 'bonus',             label: 'Bonus',             risk: 'low' },
  { v: 'pension_payout',    label: 'Pension payout',    risk: 'low' },
  { v: 'dividends',         label: 'Dividends',         risk: 'high' },
  { v: 'inheritance',       label: 'Inheritance',       risk: 'low' },
  { v: 'sale_of_house',     label: 'Sale of house',     risk: 'low' },
  { v: 'sale_of_livestock', label: 'Sale of livestock', risk: 'high' },
  { v: 'sale_of_car',       label: 'Sale of car',       risk: 'high' },
  { v: 'sale_of_shares',    label: 'Sale of shares',    risk: 'low' },
  { v: 'profit_sharing',    label: 'Profit sharing',    risk: 'high' }
];

// Product
export const PRODUCT_TYPES = [
  { v: 'unit_trust',          label: 'Unit Trust',          risk: 'high' },
  { v: 'asset_management',    label: 'Asset management',    risk: 'high' },
  { v: 'other_investments',   label: 'Other investments',   risk: 'high' },
  { v: 'short_term_loan',     label: 'Short term loan',     risk: 'medium' },
  { v: 'term_loan',           label: 'Term loan',           risk: 'low' },
  { v: 'home_loan',           label: 'Home loan',           risk: 'low' },
  { v: 'vehicle_financing',   label: 'Vehicle financing',   risk: 'low' },
  { v: 'transaction_account', label: 'Transaction account', risk: 'high' },
  { v: 'sale',                label: 'Sale',                risk: 'high' },
  { v: 'rental',              label: 'Rental',              risk: 'low' }
];

export const DELIVERY_CHANNELS = [
  { v: 'face_to_face', label: 'Face to Face', risk: 'low' },
  { v: 'online',       label: 'Online',       risk: 'high' }
];

// Legal person
export const INSTITUTION_TYPES = [
  { v: 'pty_ltd',                 label: 'Pty',                     risk: 'medium' },
  { v: 'cc',                      label: 'CC',                      risk: 'high' },
  { v: 'trust',                   label: 'Trust',                   risk: 'high' },
  { v: 'other_legal_arrangement', label: 'Other legal arrangement', risk: 'medium' }
];

export const NATURE_OF_BUSINESS = [
  { v: 'buying_selling_goods',      label: 'Buying and selling of goods', risk: 'high' },
  { v: 'manufacturing',             label: 'Manufacturing',               risk: 'medium' },
  { v: 'financial_services',        label: 'Financial services',          risk: 'high' },
  { v: 'other_services',            label: 'Other services',              risk: 'medium' },
  { v: 'agriculture',               label: 'Agriculture',                 risk: 'medium' },
  { v: 'mining',                    label: 'Mining',                      risk: 'high' },
  { v: 'oil_and_gas',               label: 'Oil and gas',                 risk: 'high' },
  { v: 'inter_vivos_trust',         label: 'Inter vivos trust',           risk: 'high' },
  { v: 'testamentary_trust',        label: 'Testamentary trust',          risk: 'low' },
  { v: 'family_trust',              label: 'Family trust',                risk: 'medium' },
  { v: 'business_investment_trust', label: 'Business/investment trust',   risk: 'high' },
  { v: 'charitable_trust',          label: 'Charitable trust',            risk: 'medium' }
];

export const BO_PIP_OPTIONS = [
  { v: 'no',        label: 'BO-PIP-No',        risk: 'low' },
  { v: 'yes',       label: 'BO-PIP-Yes',       risk: 'high' },
  { v: 'associate', label: 'BO-PIP-Associate', risk: 'high' }
];

export const RISK_LABEL = { low: 'Low', medium: 'Medium', high: 'High' };

export const optionLabel = (list, v) => list.find((o) => o.v === v)?.label || v || '—';

// Suggested nationality category for a country: Namibia -> Namibian, FATF
// black list -> Foreign Black Listed, otherwise Foreign. Grey listing is
// chosen by the user. Mirrors categoryFromCountry on the server.
const BLACKLISTED = ['NORTH KOREA', "DEMOCRATIC PEOPLE'S REPUBLIC OF KOREA", 'DPRK', 'KP',
  'IRAN', 'IRAN, ISLAMIC REPUBLIC OF', 'IR', 'MYANMAR', 'MM'];
export function categoryFromCountry(country) {
  if (!country) return '';
  const c = String(country).trim().toUpperCase();
  if (c === 'NAMIBIA' || c === 'NA' || c === 'NAMIBIAN') return 'namibian';
  if (BLACKLISTED.includes(c)) return 'foreign_black_listed';
  return 'foreign';
}

// Factor names as they appear on the sheet.
export const FACTOR_LABELS = {
  client: {
    nationality:                 'Nationality',
    pip:                         'PIP',
    pip_associate:               'Associate of a PIP',
    occupation:                  'Occupation',
    source_of_income:            'Source of Income',
    transaction_source_of_funds: 'Source of Funds for the Transaction'
  },
  product: {
    product_type:     'Product type',
    delivery_channel: 'Delivery channel'
  },
  legal_person: {
    institution_type:   'Institution',
    nature_of_business: 'Nature of Business',
    bo_pip:             'BO',
    bo_nationality:     'BO Nationality'
  }
};
